import React from 'react';

import { Checkbox, FormControl, InputLabel, ListItemText, MenuItem, Select, Stack } from '@mui/material';

import {
  PROMO_REQUEST_INPUT_TYPE_OPTIONS,
  PROMO_REQUEST_OUTPUT_TYPE_OPTIONS,
  PromoRequestInputType,
  PromoRequestOutputType,
} from '~/services/PromoService';

const inputTypeLabels: Record<PromoRequestInputType, string> = {
  promo: 'Promo code',
  voucher: 'Voucher',
  'channel-markup': 'Channel markup',
  'channel-markup-v2': 'Channel markup V2',
};

const outputTypeLabels: Record<PromoRequestOutputType, string> = {
  promo: 'Promo code',
  voucher: 'Voucher',
};

type PromoRequestTypeFiltersProps = {
  inputTypes?: Array<PromoRequestInputType>;
  outputTypes?: Array<PromoRequestOutputType>;
  onInputTypesChange: (inputTypes?: Array<PromoRequestInputType>) => void;
  onOutputTypesChange: (outputTypes?: Array<PromoRequestOutputType>) => void;
};

function getSelectedValues<T extends string>(value: string | string[], options: ReadonlyArray<T>): Array<T> {
  const selectedValues = typeof value === 'string' ? value.split(',') : value;
  return selectedValues.filter((selectedValue): selectedValue is T =>
    options.some((option) => option === selectedValue),
  );
}

function PromoRequestTypeFilters({
  inputTypes = [],
  outputTypes = [],
  onInputTypesChange,
  onOutputTypesChange,
}: PromoRequestTypeFiltersProps) {
  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
      <FormControl size="small" sx={{ minWidth: 220 }}>
        <InputLabel id="promo-request-input-types-label">Input contains</InputLabel>
        <Select
          multiple
          labelId="promo-request-input-types-label"
          id="promo-request-input-types"
          value={inputTypes}
          label="Input contains"
          renderValue={(selected) => selected.map((value) => inputTypeLabels[value]).join(', ')}
          onChange={(event) => {
            const selectedInputTypes = getSelectedValues(event.target.value, PROMO_REQUEST_INPUT_TYPE_OPTIONS);
            onInputTypesChange(selectedInputTypes.length ? selectedInputTypes : undefined);
          }}
        >
          {PROMO_REQUEST_INPUT_TYPE_OPTIONS.map((value) => (
            <MenuItem key={value} value={value}>
              <Checkbox checked={inputTypes.includes(value)} />
              <ListItemText primary={inputTypeLabels[value]} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl size="small" sx={{ minWidth: 220 }}>
        <InputLabel id="promo-request-output-types-label">Output contains</InputLabel>
        <Select
          multiple
          labelId="promo-request-output-types-label"
          id="promo-request-output-types"
          value={outputTypes}
          label="Output contains"
          renderValue={(selected) => selected.map((value) => outputTypeLabels[value]).join(', ')}
          onChange={(event) => {
            const selectedOutputTypes = getSelectedValues(event.target.value, PROMO_REQUEST_OUTPUT_TYPE_OPTIONS);
            onOutputTypesChange(selectedOutputTypes.length ? selectedOutputTypes : undefined);
          }}
        >
          {PROMO_REQUEST_OUTPUT_TYPE_OPTIONS.map((value) => (
            <MenuItem key={value} value={value}>
              <Checkbox checked={outputTypes.includes(value)} />
              <ListItemText primary={outputTypeLabels[value]} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Stack>
  );
}

export default PromoRequestTypeFilters;
